import { getApiUrl } from '@/lib/api-config';

/** An API failure with what a screen needs to react to it: the message to show, a stable `code`, the HTTP status and the server's details. */
export class ApiError extends Error {
  code: string | null;
  status: number;
  details: Record<string, any> | null;
  constructor(message: string, { code = null, status = 0, details = null }: { code?: string | null; status?: number; details?: Record<string, any> | null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function send<T>(path: string, init: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(getApiUrl(path), init);
  } catch (error) {
    if ((error as { name?: string })?.name === 'AbortError') throw error;
    throw new ApiError('The server could not be reached. Check your connection and try again.');
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    const gate = typeof payload?.message === 'string' ? payload.message : null;
    const text = gate || (typeof payload?.error === 'string' ? payload.error : null) || payload?.error?.message || 'The request could not be completed';
    const code = typeof payload?.code === 'string' ? payload.code : (gate && typeof payload?.error === 'string' ? payload.error : null);
    throw new ApiError(text, { code, status: response.status, details: payload?.details || null });
  }
  return payload.data as T;
}

/**
 * One JSON request to the KrewUp API (the session token is attached by lib/authenticated-fetch). The backend answers either
 * `{ success: true, data }` or an error with `error` (message) + `code`; the AI gate answers `{ error: code, message }`.
 * Both shapes end up as an ApiError whose `message` is safe to show.
 */
export function apiRequest<T>(path: string, { method = 'GET', body, signal }: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  return send<T>(path, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
}

/**
 * A multipart upload (a file plus its form fields) -- the ONLY way a file leaves the browser: the server stores the bytes, the browser
 * never talks to Storage. The Content-Type is left to the browser so the multipart boundary is set. Same errors as `apiRequest`.
 */
export function apiUpload<T>(path: string, form: FormData): Promise<T> {
  return send<T>(path, { method: 'POST', body: form });
}
